'use client';

/**
 * WorkspaceSwitcher — dropdown shown at the top of the sidebar.
 *
 *   - Lists every workspace (clients) from /api/workspaces
 *   - Highlights the active one
 *   - Click a workspace → POST /api/workspaces/active → reload to swap data
 *   - "+ Add Client" → opens an inline form, POST creates the workspace,
 *     auto-switches into it
 *
 * The reload after switching is intentional: every cached page / list /
 * counter is now scoped to the new workspace, so a full reload is the
 * cleanest way to make the whole app reflect that.
 */

import { useEffect, useRef, useState } from 'react';

export default function WorkspaceSwitcher({ collapsed }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState('default');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/workspaces')
      .then(r => r.json())
      .then(d => {
        if (!alive || !d?.success) return;
        setWorkspaces(d.workspaces || []);
        setActiveId(d.activeId || 'default');
      })
      .catch(() => { /* offline / first boot */ });
    return () => { alive = false; };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setAdding(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = workspaces.find(w => w.id === activeId) || { name: 'My Agency', accent: '#2563eb' };

  async function switchTo(id) {
    if (id === activeId) { setOpen(false); return; }
    setBusy(true);
    try {
      await fetch('/api/workspaces/active', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      window.location.reload();
    } catch (_) {
      setBusy(false);
    }
  }

  async function createClient() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await fetch('/api/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (data?.success && data?.workspace?.id) {
        await fetch('/api/workspaces/active', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: data.workspace.id }),
        });
        window.location.reload();
      } else {
        setBusy(false);
      }
    } catch (_) {
      setBusy(false);
    }
  }

  // Collapsed sidebar: render a compact dot
  if (collapsed) {
    return (
      <button
        title={`Active client: ${active.name}`}
        onClick={() => setOpen(!open)}
        style={{
          width: 36, height: 36, borderRadius: 8,
          background: active.accent || 'var(--ink)',
          color: 'var(--paper)', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 800,
        }}
      >
        {active.name.slice(0, 2).toUpperCase()}
      </button>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginTop: 6 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--bg-card)',
          border: '1px solid var(--hairline)',
          borderRadius: 8,
          color: 'var(--text-primary)',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'all 0.18s',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 22, height: 22, borderRadius: 6,
            background: active.accent || 'var(--ink)',
            color: 'var(--paper)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 800,
          }}
        >
          {active.name.slice(0, 2).toUpperCase()}
        </span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {active.name}
        </span>
        <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0, right: 0,
            background: 'var(--bg-card)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-lg)',
            zIndex: 200,
            overflow: 'hidden',
            maxHeight: 360,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: 8, overflowY: 'auto', flex: 1 }}>
            {workspaces.map(w => (
              <button
                key={w.id}
                onClick={() => switchTo(w.id)}
                disabled={busy}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px',
                  background: w.id === activeId ? 'var(--bg-subtle)' : 'transparent',
                  border: '1px solid ' + (w.id === activeId ? 'var(--hairline-2)' : 'transparent'),
                  borderRadius: 8,
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  fontWeight: w.id === activeId ? 700 : 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  marginBottom: 2,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 20, height: 20, borderRadius: 5,
                    background: w.accent || 'var(--ink)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 800, color: 'var(--paper)',
                  }}
                >
                  {w.name.slice(0, 2).toUpperCase()}
                </span>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {w.name}
                </span>
                {w.id === activeId && <span style={{ fontSize: 10, color: 'var(--blue)', fontWeight: 700 }}>ACTIVE</span>}
              </button>
            ))}
          </div>

          <div style={{ padding: 8, borderTop: '1px solid var(--hairline)' }}>
            {adding ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  autoFocus
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createClient(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  placeholder="Client name e.g. Acme Dental"
                  style={{
                    flex: 1,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--hairline-2)',
                    borderRadius: 6,
                    padding: '8px 10px',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    outline: 'none',
                  }}
                />
                <button
                  onClick={createClient}
                  disabled={busy || !newName.trim()}
                  style={{
                    background: 'var(--ink)',
                    color: 'var(--paper)', border: 'none', borderRadius: 999,
                    padding: '8px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer',
                  }}
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px dashed var(--hairline-2)',
                  borderRadius: 6,
                  padding: '8px 10px',
                  color: 'var(--blue)',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Add Client
              </button>
            )}
            <a
              href="/clients"
              onClick={() => setOpen(false)}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '6px 10px',
                marginTop: 6,
                color: 'var(--ink-2)',
                fontSize: 11,
                textDecoration: 'none',
              }}
            >
              Manage all clients →
            </a>
            <button
              onClick={async () => {
                setBusy(true);
                try {
                  await fetch('/api/demo', { method: 'POST' });
                  window.location.reload();
                } catch (_) { setBusy(false); }
              }}
              disabled={busy}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: '6px 10px',
                color: 'var(--ink-3)',
                fontSize: 10,
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              🎭 Load demo data (sales call)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}